import { BuildingsIcon } from "@phosphor-icons/react/Buildings";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { AuthenticationSwitchOrganizationDialog } from "@/Features/Authentication/Exports/Dialogs/AuthenticationSwitchOrganizationDialog";
import { useUserService } from "@/Services/UserService";

function NavBarSwitchOrganizationDialog_(props: DialogBaseProps) {
  const { user } = useAuthentication();
  const { switchToLinkedCompany } = useUserService();
  const linkedCompanies = user?.linkedCompanies ?? [];

  // The user guard sits inside the Dialog's children, not in front of it: if
  // user is briefly undefined (auth refresh) the Dialog stays mounted with
  // empty content instead of being silently destroyed while open.
  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      width="narrow"
      title="Switch Organization"
    >
      <ControlPresentationProvider presentation="underlined">
        {user && (
          <AuthenticationSwitchOrganizationDialog
            linkedOrganizations={[...linkedCompanies, user.company].filter(
              Boolean,
            )}
            onSwitchOrganization={async (organizationId: string) => {
              const result = await switchToLinkedCompany(organizationId);
              if (result.success) {
                window.location.reload();
              }
              return result;
            }}
            onFinished={() => props.onOpenChange?.(false)}
          />
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}

/**
 * "Switch Organization" item for the user menu. Only renders when the user
 * has linked organizations, and owns the switch dialog + mutation itself
 * (menu items must encapsulate their handler, not receive it via props).
 */
export function NavBarSwitchOrganizationButton() {
  const { user } = useAuthentication();
  const hasLinkedOrganizations = (user?.linkedCompanies ?? []).length > 0;

  if (!hasLinkedOrganizations) {
    return <></>;
  }

  return (
    <Button
      icon={BuildingsIcon}
      data-testid="switch-organization-menu-option"
      dialog={() => <NavBarSwitchOrganizationDialog_ />}
    >
      Switch Organization
    </Button>
  );
}
